<script lang="ts" setup>
import LearningItemForm from '@/components/learning/LearningItemForm.vue';
import LearningImportForm from '@/components/learning/LearningImportForm.vue';
import { Mutations, ShiftCMSState } from '@/store';
import { computed, markRaw, onBeforeUnmount, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { format } from 'date-fns';
import { LearningActions, LearningState } from '@/stores/learning.store';
import {
  LearningItem,
  LearningType,
  getLearningType,
} from '@/models/learning-item.model';
import { useEvents, Event } from '@/services/events.composable';
import TranslatedState from '../common/TranslatedState.vue';
import ListFilter from '../common/ListFilter.vue';

const store = useStore<ShiftCMSState & { les: LearningState }>();
const items = computed<LearningItem[]>(() => store.state.les.items);
const events = useEvents();

const update = () => {
  store.dispatch(LearningActions.GetLearningItems);
};

onMounted(() => {
  update();
  events.on(Event.LanguageChanged, update);
});

onBeforeUnmount(() => {
  store.commit(Mutations.HideDrawer);
  events.off(Event.LanguageChanged, update);
});

const editLearningItem = (_: Event, { item }: { item: LearningItem }) => {
  store.commit(Mutations.ShowDrawer, {
    component: markRaw(LearningItemForm),
    data: item,
  });
};

const addLearningItem = () => {
  store.commit(Mutations.ShowDrawer, { component: markRaw(LearningItemForm) });
};
const importLearning = () => {
  store.commit(Mutations.ShowDrawer, {
    component: markRaw(LearningImportForm),
  });
};

const headers = [
  { title: 'ID', key: 'id', sortable: false, width: '5%' },
  { title: 'Title', key: 'title', width: '25%' },
  { title: 'Type', key: 'type', width: '15%' },
  { title: 'Category', key: 'mainCategory.title', width: '25%' },
  { title: 'Date', key: 'creationDate', width: '20%' },
  { title: 'State', key: 'translated', width: '10%' },
];

const filter = ref<string>('');
const filterTitle = ref<string>('');
const filteredItems = computed<LearningItem[]>(() => {
  return items.value.filter((v: LearningItem) => {
    if (filterTitle.value) {
      return [v.title, v.description, v.recommendation, v.type]
        .map((s) => s.toLowerCase())
        .join('')
        .includes(filterTitle.value);
    }
    if (filter.value) {
      return v.type === filter.value;
    }

    return true;
  });
});

function updateFilter(filters: { filter: string; filterTitle: string }) {
  filter.value = filters.filter;
  filterTitle.value = filters.filterTitle;

  console.log(filters.filter, filters.filterTitle);
}
</script>

<!-- { label: 'Article', value: LearningType.Article },
        { label: 'Book', value: LearningType.Book },
        { label: 'Building', value: LearningType.Building },
        { label: 'Experience', value: LearningType.Experience },
        { label: 'FaceBookGroup', value: LearningType.FaceBookGroup },
        { label: 'Influencer', value: LearningType.Influencer },
        { label: 'InstagramChannel', value: LearningType.InstagramChannel },
        { label: 'WebsiteOrApp', value: LearningType.WebsiteOrApp },
        { label: 'Movie', value: LearningType.Movie },
        { label: 'Podcast', value: LearningType.Podcast },
        { label: 'TEDTalk', value: LearningType.TEDTalk },
        { label: 'Visual', value: LearningType.Visual },
        { label: 'Video', value: LearningType.Video },
        { label: 'Other', value: LearningType.Other }, -->
<template>
  <v-container>
    <h2>Learning Items</h2>
    <ListFilter
      dense
      :options="[
        { label: 'Article', value: LearningType.Article },
        { label: 'Book', value: LearningType.Book },
        { label: 'Building', value: LearningType.Building },
        { label: 'Experience', value: LearningType.Experience },
        { label: 'FaceBookGroup', value: LearningType.FaceBookGroup },
        { label: 'Influencer', value: LearningType.Influencer },
        { label: 'InstagramChannel', value: LearningType.InstagramChannel },
        { label: 'WebsiteOrApp', value: LearningType.WebsiteOrApp },
        { label: 'Movie', value: LearningType.Movie },
        { label: 'Podcast', value: LearningType.Podcast },
        { label: 'TEDTalk', value: LearningType.TEDTalk },
        { label: 'Visual', value: LearningType.Visual },
        { label: 'Video', value: LearningType.Video },
        { label: 'Other', value: LearningType.Other },
      ]"
      @filter="updateFilter"
    />
    <v-data-table
      density="compact"
      @click:row="editLearningItem"
      :headers="headers"
      :items="filteredItems"
      itemsPerPage="25"
      hover
    >
      <template v-slot:item.creationDate="{ value }">
        {{ format(value, 'do MMMM yyyy - HH:mm') }}
      </template>
      <template v-slot:item.type="{ value }">
        {{ getLearningType(value) }}
      </template>
      <template v-slot:item.translated="{ item }">
        <div style="display: flex; flex-direction: row">
          <translated-state :item="item" />
          &nbsp;
          <v-tooltip
            :text="
              item.published
                ? 'This item is published'
                : 'This item is not published'
            "
          >
            <template v-slot:activator="{ props }">
              <v-icon
                v-bind="props"
                :icon="item.published ? 'mdi-eye-check' : 'mdi-eye-outline'"
                :color="item.published ? 'green' : 'red'"
              ></v-icon>
            </template>
          </v-tooltip>
          &nbsp;
          <v-tooltip text="This item is recommended">
            <template v-slot:activator="{ props }">
              <v-icon
                v-bind="props"
                icon="mdi-thumb-up-outline"
                color="primary"
                v-if="!!item.recommended"
              ></v-icon>
            </template>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>

    <v-btn @click="addLearningItem" color="success">Add Learning Item</v-btn
    >&nbsp;
    <v-btn @click="importLearning" color="primary">Import Learning Item</v-btn>
  </v-container>
</template>
